// Pause screen (UI-3): Resume / Help / Cheats toggle / Options / Save & Quit.
// Shown when pointer lock is released mid-game.
import { h } from '../h';

export interface PauseActions {
  onResume: () => void;
  onHelp: () => void;
  onStats: () => void;
  onOptions: () => void;
  onSaveQuit: () => void;
  /** Current cheats state + toggle (persists to the world meta). */
  cheatsEnabled: () => boolean;
  onToggleCheats: () => void;
  /** Open the local storage read-out (STO-3, M41). */
  onStorage?: () => void;
  /** Open the registry/content editor. */
  onEditor?: () => void;
  /** DOR-5 escape hatch: return to the home world; only shown when there's one to go to. */
  onReturnHome?: () => void;
  /** Hangar §39.6 (M106.5, D-780 Q2): only shown bridged, on a server world, owner/instructor. */
  onInviteConnection?: () => void;
  /** Hangar §39.8 (M108.2): only shown bridged, on a server world. */
  onWorldManager?: () => void;
}

export function showPause(actions: PauseActions): HTMLElement {
  const root = h('div', { class: 'bb-screen bb-pause' });
  document.body.append(root);
  const close = (fn: () => void) => () => {
    document.removeEventListener('keydown', onDown, true);
    document.removeEventListener('keyup', onUp, true);
    root.remove();
    fn();
  };

  // Escape resumes — but on key RELEASE, and only for a press that started while
  // the menu was already open. Both halves matter:
  //
  //  • Escape is what exits pointer lock in the first place, and that is what
  //    opens this menu. Closing on keydown meant auto-repeat closed it again,
  //    re-locked the pointer, and the still-held key exited the lock once more —
  //    the menu flickered open/shut for as long as the key was down.
  //  • Closing on any keyup is not enough either: the keydown that opened the
  //    menu is followed by its own keyup, which would shut the menu the instant
  //    the player let go of the very key that summoned it. `armed` ignores that
  //    trailing release, because its keydown happened before this listener existed.
  let armed = false;
  const onDown = (e: KeyboardEvent): void => {
    if (e.code !== 'Escape') return;
    e.preventDefault();
    e.stopPropagation();
    armed = true;
  };
  const onUp = (e: KeyboardEvent): void => {
    if (e.code !== 'Escape' || !armed) return;
    e.preventDefault();
    e.stopPropagation();
    close(actions.onResume)();
  };
  document.addEventListener('keydown', onDown, true);
  document.addEventListener('keyup', onUp, true);

  const cheats = h('button', { class: 'bb-btn' }, cheatsLabel(actions.cheatsEnabled()));
  cheats.onclick = () => {
    actions.onToggleCheats();
    cheats.textContent = cheatsLabel(actions.cheatsEnabled());
  };

  const extra: HTMLElement[] = [];
  if (actions.onReturnHome) extra.push(h('button', { class: 'bb-btn', 'data-testid': 'pause-return-home', onclick: close(actions.onReturnHome) }, 'Return to Home World'));
  if (actions.onStorage) extra.push(h('button', { class: 'bb-btn', 'data-testid': 'pause-storage', onclick: close(actions.onStorage) }, 'Storage…'));
  if (actions.onEditor) extra.push(h('button', { class: 'bb-btn', 'data-testid': 'pause-editor', onclick: close(actions.onEditor) }, 'Content Editor…'));
  if (actions.onInviteConnection) extra.push(h('button', { class: 'bb-btn', 'data-testid': 'pause-invite', onclick: close(actions.onInviteConnection) }, 'Invite a connection…'));
  if (actions.onWorldManager) extra.push(h('button', { class: 'bb-btn', 'data-testid': 'pause-world-manager', onclick: close(actions.onWorldManager) }, 'World manager…'));

  root.append(
    h('div', { class: 'bb-title' }, 'Paused'),
    h(
      'div',
      { class: 'bb-panel' },
      h('button', { class: 'bb-btn bb-primary', onclick: close(actions.onResume) }, 'Resume'),
      h('button', { class: 'bb-btn', onclick: close(actions.onHelp) }, 'How to Play'),
      h('button', { class: 'bb-btn', onclick: close(actions.onStats) }, 'Statistics'),
      cheats,
      ...extra,
      h('button', { class: 'bb-btn', onclick: close(actions.onOptions) }, 'Options'),
      h('button', { class: 'bb-btn bb-danger', onclick: close(actions.onSaveQuit) }, 'Save & Quit'),
    ),
  );
  return root;
}

function cheatsLabel(on: boolean): string {
  return `Cheats: ${on ? 'On' : 'Off'}`;
}
